import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import { netzBeobachten, netzNeuBewerten, netzStand } from '../api/client'
import { abonnieren, alle, korbBesitzer, senden, type OutboxEntry } from '../lib/outbox'

/**
 * Was in diesem Verlauf noch auf Netz wartet.
 *
 * Bewusst kein TanStack-Query: der Ausgangskorb ist kein Serverzustand, den
 * man neu holen könnte, sondern das Gegenteil davon -- die Wahrheit liegt im
 * Gerät, und geändert wird sie nur von hier.
 */
export function useOutbox(tripId: number, withUser?: number, entwurf = false): OutboxEntry[] {
  const [eintraege, setEintraege] = useState<OutboxEntry[]>([])

  useEffect(() => {
    let lebt = true
    const lesen = () => {
      void alle().then((liste) => {
        if (lebt) setEintraege(liste.filter((e) => passt(e, tripId, withUser, entwurf)))
      })
    }

    lesen()
    const abbestellen = abonnieren(lesen)
    return () => {
      lebt = false
      abbestellen()
    }
  }, [tripId, withUser, entwurf])

  return eintraege
}

/**
 * Was in **diesen** Verlauf gehört.
 *
 * Ein wartendes Highlight gehört in keinen: es ist kein Beitrag, und als
 * Sprechblase im Chat wäre es eine Nachricht, die nie eine war. Es steht auf
 * der Karte -- und im Streifen oben, der alles Wartende zählt.
 */
function passt(eintrag: OutboxEntry, tripId: number, withUser?: number, entwurf = false): boolean {
  return (
    (eintrag.art ?? 'nachricht') === 'nachricht'
    && eintrag.tripId === tripId
    && (eintrag.withUser ?? undefined) === withUser
    // A waiting draft stands in the draft (#85), not in the chat.
    && (eintrag.entwurf ?? false) === entwurf
  )
}

/**
 * Der Postbote: leert den Korb, wann immer sich eine Gelegenheit bietet.
 *
 * Drei Auslöser, und der dritte ist der auf iOS wichtigste: `online` kommt
 * beim Zurückkehren aus dem Hintergrund nicht verlässlich, `visibilitychange`
 * schon. Wer die App wieder aufmacht, ist genau der Augenblick, in dem eine
 * wartende Nachricht rausgehen soll.
 */
export function useOutboxVersand(userId: number | null): void {
  const client = useQueryClient()

  useEffect(() => {
    // The basket is the signed-in account's (1.187.0, review 2026-10b B2);
    // a new account first sorts out what is not its own.
    korbBesitzer(userId)
    if (userId === null) return
    const versuchen = () => {
      void senden().then((raus) => {
        // Was rausging, ist jetzt eine richtige Nachricht -- der Verlauf und
        // die Liste sollen das sofort zeigen und nicht erst beim nächsten
        // Takt. Betroffen ist nur, was gerade offen ist; alles andere holt
        // sich seinen Stand ohnehin beim Öffnen.
        // **Und hier ebenfalls alles** (Codereview C11): im Korb liegen
        // Beiträge, Kartenpunkte und ihre Fotos nebeneinander, und welche
        // Art gerade rausging, weiß diese Stelle nicht. Sie läuft selten --
        // wenn Netz wiederkommt -- und nicht bei jedem Reglerschritt.
        if (raus > 0) void client.invalidateQueries()
      })
    }

    /*
     * **Erst die Marke zurücknehmen, dann versuchen** (1.39.0).
     *
     * `senden()` steigt bei „kein Netz" sofort wieder aus -- und diese Marke
     * setzt ausgerechnet ein abgebrochener Upload selbst. Ohne die Zeile hier
     * passierte beim Zurückkommen in die App deshalb **gar nichts**: der Korb
     * fragte eine Auskunft ab, die sein eigener letzter Fehlschlag
     * hinterlassen hatte. Nur ein Neustart half, weil der die Marke ohnehin
     * auf „ja" stellt. Genau so wurde es gemeldet.
     *
     * Die drei Gelegenheiten hier sind die, bei denen sich die Lage geändert
     * haben kann. Der Versuch danach entscheidet, nicht die Marke.
     *
     * **Nicht bei `netzBeobachten`**, und das ist kein Versehen: der Hörer
     * läuft, wenn sich die Marke ändert. Würde er sie dabei selbst wieder
     * setzen, drehte sich das im Kreis.
     */
    const vonVorn = () => {
      netzNeuBewerten()
      versuchen()
    }

    vonVorn()
    window.addEventListener('online', vonVorn)
    const beiSicht = () => {
      if (document.visibilityState === 'visible') vonVorn()
    }
    document.addEventListener('visibilitychange', beiSicht)
    // Und wenn irgendeine andere Anfrage wieder durchkommt: das ist der
    // verlässlichste Hinweis darauf, dass es wieder geht -- verlässlicher als
    // das `online`-Ereignis, das nur sagt, dass ein Netz da ist.
    const abbestellen = netzBeobachten(versuchen)

    return () => {
      window.removeEventListener('online', vonVorn)
      document.removeEventListener('visibilitychange', beiSicht)
      abbestellen()
    }
  }, [client, userId])
}

/**
 * Welche Highlights dieser Reise noch auf Netz warten.
 *
 * Sie stehen beim Server noch nicht, also kann die Karte sie nicht von dort
 * holen -- ohne diesen Haken sähe es aus, als wäre das Anlegen ins Leere
 * gegangen.
 *
 * **Bis 1.46.0 gab er nur eine Zahl zurück**, und daneben stand ein Satz
 * „ein Highlight wartet auf Netz". Aus dem Betrieb: „es sollte zumindest das
 * Kärtchen angezeigt werden." Stimmt -- der Chat zeigt eine blasse
 * Sprechblase an der Stelle, an der der Beitrag gleich stehen wird, und die
 * Karte zeigte eine Zeile. Für ein Kärtchen braucht es den Eintrag selbst;
 * die Zahl ist `länge` und deshalb keine zweite Auskunft.
 */
export function useWartendeHighlights(tripId: number): OutboxEntry[] {
  const [eintraege, setEintraege] = useState<OutboxEntry[]>([])

  useEffect(() => {
    let lebt = true
    const lesen = () => {
      void alle().then((liste) => {
        if (lebt) {
          setEintraege(liste.filter((e) => e.art === 'highlight' && e.tripId === tripId))
        }
      })
    }

    lesen()
    const abbestellen = abonnieren(lesen)
    return () => {
      lebt = false
      abbestellen()
    }
  }, [tripId])

  return eintraege
}

/**
 * The weather cards that wait for the net in **this** thread (#98): the
 * chat's or the draft's. Like `useWartendeHighlights`, and separate from
 * `useOutbox` on purpose -- a waiting card is drawn as a card, not as a
 * bubble with a text.
 *
 * **`null` until the basket has been read** (1.54.2), and that is not a
 * nicety: the basket lives in IndexedDB and answers a moment later, so an
 * empty list at the first paint means "not looked yet", not "nothing
 * waits". Whoever acts on the empty case -- the status line above the
 * composer does -- would act once too early, and did.
 */
export function useWartendesWetter(tripId: number, entwurf = false): OutboxEntry[] | null {
  return useWartendeArt(tripId, entwurf, 'wetter')
}

/**
 * The polls that wait for the net in this thread (1.80.0, from use:
 * "Abstimmungen auch offline") -- the same road as a waiting weather card,
 * and `null` until the basket has been read for the same reason.
 */
export function useWartendeAbstimmungen(tripId: number, entwurf = false): OutboxEntry[] | null {
  return useWartendeArt(tripId, entwurf, 'abstimmung')
}

/**
 * Entries of one kind that wait in this thread (the chat's or the draft's) --
 * the one place both hooks above ask (1.80.0). A card is drawn as a card, not
 * as a bubble with text, which is why this is separate from `useOutbox`.
 */
function useWartendeArt(
  tripId: number,
  entwurf: boolean,
  art: 'wetter' | 'abstimmung',
): OutboxEntry[] | null {
  const [eintraege, setEintraege] = useState<OutboxEntry[] | null>(null)

  useEffect(() => {
    let lebt = true
    const lesen = () => {
      void alle().then((liste) => {
        if (lebt) {
          setEintraege(liste.filter((e) => e.art === art && e.tripId === tripId && (e.entwurf ?? false) === entwurf))
        }
      })
    }

    lesen()
    const abbestellen = abonnieren(lesen)
    return () => {
      lebt = false
      abbestellen()
    }
  }, [tripId, entwurf, art])

  return eintraege
}

/** Wie viele Nachrichten insgesamt warten -- für den Streifen oben. */
export function useOutboxAnzahl(): number {
  const [anzahl, setAnzahl] = useState(0)

  useEffect(() => {
    let lebt = true
    const lesen = () => {
      void alle().then((liste) => {
        if (lebt) setAnzahl(liste.length)
      })
    }

    lesen()
    const abbestellen = abonnieren(lesen)
    return () => {
      lebt = false
      abbestellen()
    }
  }, [])

  return anzahl
}

/**
 * Ob gerade etwas durchgeht.
 *
 * Zwei Quellen, weil eine nicht reicht: die Ereignisse des Browsers
 * (`online`/`offline`) sagen es sofort, wenn jemand den Flugmodus umlegt --
 * behaupten aber „online", sobald irgendein WLAN da ist. Was tatsächlich
 * ankommt, weiß nur `netzStand()` aus den Anfragen selbst.
 */
export function useOnline(): boolean {
  const [online, setOnline] = useState(netzStand)

  useEffect(() => {
    const pruefen = () => setOnline(netzStand())

    // Ein Wechsel auf „online" ist eine Behauptung, keine Zusage: der nächste
    // Abruf zeigt, ob wirklich etwas geht. Deshalb wird hier nur neu bewertet.
    window.addEventListener('online', pruefen)
    window.addEventListener('offline', pruefen)
    const abbestellen = netzBeobachten(pruefen)

    pruefen()
    return () => {
      window.removeEventListener('online', pruefen)
      window.removeEventListener('offline', pruefen)
      abbestellen()
    }
  }, [])

  return online
}
